import React, { useState, useEffect } from 'react';
import { QrCode, Copy, User, PlayCircle, AlertCircle, Edit, Hash, Calendar, CheckCircle } from 'lucide-react';
import LoadingSpinner from './ui/LoadingSpinner';
import Button from './ui/Button';
import Card from './ui/Card.jsx';
import SegmentedControl from './ui/SegmentedControl.jsx';
import QrPaper from './ui/QrPaper.jsx';
import Input from './ui/Input.jsx';
import { toast } from './notifications';
import ParticipantsList from './session/ParticipantsList';
import ShareSessionCard from './session/ShareSessionCard.jsx';
import { apiService } from '../services/api';
import { PARTICIPANT_STATUS, SESSION_STATUS } from '../constants';
import { useNavigate } from 'react-router-dom';

const WaitingSessionStatus = ({ session, onValidate, onCancel, isHost, onStart, currentUserId, onRefresh }) => {
  const navigate = useNavigate();
  const [validationCode, setValidationCode] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [showQR, setShowQR] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  // Sperre während POST /start läuft: Ein Doppel-Tap im Schul-WLAN schickte sonst
  // zwei Starts los (Review sessions-3; der Server lehnt den zweiten jetzt mit 409 ab).
  const [isStarting, setIsStarting] = useState(false);

  // Session-Code: Backend liefert verificationCode, Fallback auf code für Kompatibilität
  const sessionCode = session?.verificationCode || session?.code || '';

  // ✅ Eigenen Teilnehmer-Status aus Backend-Daten ableiten (Source of Truth)
  const myParticipant = currentUserId
    ? (session.participants || []).find(
      p => String(p.userId) === String(currentUserId) || String(p.id) === String(currentUserId),
    )
    : null;
  const myStatus = myParticipant?.status || null;
  const isValidated = myStatus === PARTICIPANT_STATUS.VALIDATED;

  // QR-Code laden (nur für Host)
  useEffect(() => {
    if (isHost && session?.id) {
      setQrLoading(true);
      apiService.getSessionQRCode(session.id)
        .then(res => {
          setQrCode(res.data?.qrCode || res.data);
        })
        .catch(err => {
          console.error('QR-Code laden fehlgeschlagen:', err);
        })
        .finally(() => {
          setQrLoading(false);
        });
    }
  }, [isHost, session?.id]);

  // Count non-host participants who are ready (VALIDATED, PENDING, or RESERVED)
  const readyNonHostCount = session.participants?.filter(
    p => ['VALIDATED', 'PENDING', 'RESERVED'].includes(p.status) && p.userId !== session.hostId
  ).length || 0;

  const validatedNonHostCount = session.participants?.filter(
    p => p.status === PARTICIPANT_STATUS.VALIDATED && p.userId !== session.hostId
  ).length || 0;

  // Host kann starten wenn mindestens 1 anderer Teilnehmer da ist (validiert, pending oder reserviert)
  const canStart = readyNonHostCount >= 1;

  const handleStartClick = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      await onStart(session.id);
    } catch (error) {
      // Fehlermeldung zeigt onStart selbst (SessionStatus.handleSessionStart)
      console.error('Fehler beim Starten der Session:', error);
    } finally {
      setIsStarting(false);
    }
  };

  const handleKickParticipant = async (userId) => {
    try {
      await apiService.removeParticipant(session.id, userId);
      // Ohne Refresh stand die Liste hier bis zum 15s-Poll auf dem alten Stand
      // (SessionStatus macht das schon lange, dieser Pfad fehlte).
      onRefresh?.();
    } catch (error) {
      console.error('Fehler beim Entfernen des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Entfernen der Person');
    }
  };

  // Host laesst einen versehentlich entfernten Teilnehmer wieder zu
  const handleReadmitParticipant = async (userId) => {
    try {
      await apiService.readmitParticipant(session.id, userId);
      onRefresh?.();
    } catch (error) {
      console.error('Fehler beim Wieder-Zulassen des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Wieder-Zulassen');
    }
  };

  // Host markiert einen Teilnehmer manuell als anwesend (z.B. Schueler ohne eigenes Geraet)
  const handleMarkPresent = async (userId) => {
    try {
      await apiService.markParticipantPresent(session.id, userId);
      onRefresh?.();
    } catch (error) {
      console.error('Fehler beim Markieren des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Markieren als anwesend');
    }
  };

  return (
    // Keine eigene Breite und kein eigenes Seiten-Padding: Den Rahmen setzt die Seite
    // (SessionDetailPage -> PageShell). Das `max-w-2xl mx-auto px-4`, das hier bis
    // August 2026 stand, lag INNERHALB dieses Rahmens - derselbe Kasten-im-Kasten, der
    // in SessionHeader und SessionStatus schon entfernt wurde, nur eine Ebene tiefer
    // und deshalb beim ersten Durchgang uebersehen.
    <div className="space-y-3 sm:space-y-4">
      {/* Session Info Header */}
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg sm:text-xl font-bold text-foreground truncate">{session.topic}</h2>
          {/* Geplante Startzeit anzeigen */}
          {session.startTime && session.status === SESSION_STATUS.PLANNED && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mt-1">
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">
                Geplant für {new Date(session.startTime).toLocaleDateString('de-DE', {
                  weekday: 'short', day: 'numeric', month: 'short'
                })} um {new Date(session.startTime).toLocaleTimeString('de-DE', {
                  hour: '2-digit', minute: '2-digit'
                })} Uhr
              </span>
            </div>
          )}
        </div>
        {/* Edit Button - nur für Host in PLANNED Sessions */}
        {isHost && session.status === SESSION_STATUS.PLANNED && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/session/${session.id}/edit`, { state: { session } })}
            className="shrink-0"
            title="Session bearbeiten"
            aria-label="Session bearbeiten"
          >
            <Edit className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Bearbeiten</span>
          </Button>
        )}
      </div>

      {/* Session-Zugang Komponente - nur wenn User noch NICHT validiert ist */}
      {!isHost && !isValidated && (
        <Card className="p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="font-medium text-base sm:text-lg text-foreground">Session-Zugang</h3>
            <Button variant="secondary" size="sm" onClick={() => navigate('/scan-qr')} className="shrink-0">
              <QrCode className="h-4 w-4" aria-hidden="true" />
              QR scannen
            </Button>
          </div>

          <div className="flex gap-2">
            {/* ui/Input statt rohem Feld: bringt Touch-Hoehe, Token-Rahmen und den
                Fokus-Ring in Markenfarbe mit - vorher `focus:ring-gray-300`, das als
                einziges Feld der App grau aufleuchtete. */}
            <Input
              type="text"
              value={validationCode}
              onChange={(e) => setValidationCode(e.target.value.toUpperCase())}
              placeholder="Session-Code"
              aria-label="Session-Code eingeben"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              maxLength={6}
              className="flex-1 min-w-0 font-mono tracking-wider"
            />
            <Button
              onClick={async () => {
                try {
                  await onValidate(validationCode);
                  // Status kommt über Backend-Refresh / WebSocket — kein lokaler State mehr.
                } catch {
                  // Fehler wird in onValidate gehandelt (Toast)
                }
              }}
              disabled={!validationCode}
              className="shrink-0"
            >
              OK
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Gib den Session-Code ein oder scanne den QR-Code.
          </p>
        </Card>
      )}

      {/* Host einer offenen Session: Link in die Klassengruppe schicken (Nov 2026).
          Erscheint nur, solange Plätze frei sind (utils/sessionShare.js). */}
      {isHost && <ShareSessionCard session={session} currentUserId={currentUserId} />}

      {/* Host: Session teilen — Segment „# Code | QR“ wie auf der Session-Seite
          und im Spot (Okt 2026), QR auf Papier mit Scan-Ecken. */}
      {isHost && (
        <Card className="p-4 sm:p-[18px]">
          <h3 className="text-title-s font-semibold text-foreground">Session teilen</h3>
          <p className="mt-0.5 text-meta text-muted-foreground">Teile diesen Code zum Beitreten</p>
          <SegmentedControl
            size="sm"
            ariaLabel="Code oder QR-Code"
            value={showQR ? 'qr' : 'code'}
            onChange={(v) => setShowQR(v === 'qr')}
            options={[
              { value: 'code', label: 'Code', icon: Hash },
              { value: 'qr', label: 'QR', icon: QrCode },
            ]}
            className="my-3"
          />

          {showQR ? (
            <div className="flex flex-col items-center py-2">
              {qrLoading ? (
                <div className="flex h-60 w-60 items-center justify-center rounded-xl bg-surface-raised">
                  <LoadingSpinner size="default" />
                </div>
              ) : qrCode ? (
                <QrPaper animateKey={sessionCode}>
                  <img src={qrCode} alt="Session QR-Code" className="block h-56 w-56" />
                </QrPaper>
              ) : (
                <div className="flex h-60 w-60 items-center justify-center rounded-xl bg-surface-raised text-faint">
                  <QrCode className="h-10 w-10 sm:h-12 sm:w-12" aria-hidden="true" />
                </div>
              )}
              <p className="mt-3 px-2 text-center text-meta text-muted-foreground">
                Teilnehmende können diesen QR-Code scannen
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-surface-raised px-4 py-3 sm:gap-3 sm:px-6 sm:py-4">
                <code className="break-all text-center font-mono text-2xl font-bold tracking-wider sm:text-3xl">{sessionCode}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(sessionCode);
                    toast.success('Code kopiert');
                  }}
                  className="shrink-0 px-2"
                  title="Code kopieren"
                  aria-label="Session-Code kopieren"
                >
                  <Copy className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Nicht-Host: Status anzeigen — basiert auf Backend-Status (Source of Truth) */}
      {!isHost && (
        <Card className="p-3 sm:p-4">
          <h3 className="font-medium text-sm sm:text-base text-foreground mb-2">Status</h3>
          {isValidated ? (
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm sm:text-base font-medium">Du bist validiert — die Session wartet auf den Start.</span>
            </div>
          ) : myStatus === PARTICIPANT_STATUS.RESERVED ? (
            <div className="flex items-center gap-2 text-info">
              <Calendar className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm sm:text-base">Platz reserviert — bitte Code eingeben oder QR scannen.</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm sm:text-base">Warte auf Validierung...</span>
            </div>
          )}
        </Card>
      )}

      {/* Info Box - Different message based on participant count */}
      {canStart && isHost ? (
        <Card className="p-3 sm:p-4">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-success" aria-hidden="true" />
            <h3 className="font-medium text-sm sm:text-base text-foreground">Session kann gestartet werden!</h3>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {readyNonHostCount} Teilnehmende {readyNonHostCount === 1 ? 'ist bereit' : 'sind bereit'}. Du kannst die Session jetzt starten!
          </p>
        </Card>
      ) : (
        <Card className="p-3 sm:p-4">
          <h3 className="font-medium text-sm sm:text-base text-foreground mb-2">
            {isHost ? 'Warte auf die erste Person...' : 'Session noch nicht aktiv'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {isHost
              ? 'Die Session kann gestartet werden sobald sich mindestens eine teilnehmende Person validiert hat. Teile dafür den Session-Code oder QR-Code.'
              : validatedNonHostCount === 0
                ? 'Die Session startet, sobald sich weitere Teilnehmende validiert haben.'
                : `${validatedNonHostCount} weitere ${validatedNonHostCount === 1 ? 'Person ist' : 'Personen sind'} bereits validiert (außer Host).`}
          </p>
        </Card>
      )}

      {/* Participants */}
      <ParticipantsList
        participants={session.participants}
        maxParticipants={session.maxParticipants}
        isHost={isHost}
        currentUserId={currentUserId}
        onKickParticipant={isHost ? handleKickParticipant : undefined}
        onMarkPresent={isHost ? handleMarkPresent : undefined}
        onReadmitParticipant={isHost ? handleReadmitParticipant : undefined}
      />

      {/* Start Button for Host - More prominent when ready */}
      {isHost && onStart && session.status === 'PLANNED' && (
        <Button
          variant="primary"
          size="lg"
          onClick={handleStartClick}
          disabled={!canStart || isStarting}
          fullWidth
        >
          <PlayCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {readyNonHostCount === 0 ? (
              'Warte auf Teilnehmende...'
            ) : readyNonHostCount === 1 ? (
              'Session mit einer Person starten'
            ) : (
              `Session mit ${readyNonHostCount} ${readyNonHostCount === 1 ? 'teilnehmender Person' : 'Teilnehmenden'} starten`
            )}
          </span>
        </Button>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <Button variant="secondary" size="lg" onClick={onCancel} fullWidth>
          {isHost
            ? (session.status === SESSION_STATUS.PLANNED ? 'Session absagen' : 'Session abbrechen')
            : 'Teilnahme absagen'}
        </Button>
      )}

    </div>
  );
};

export default WaitingSessionStatus;